import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_URL } from '../../config/constants';

const nameKey = (token) => `workloop.cartGuestName.${token}`;

const colorFrom = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '#2563eb';
  const hex = (n >>> 0).toString(16).padStart(8, '0').slice(-6);
  return `#${hex}`;
};

export default function PublicCartPage() {
  const { token } = useParams();
  const [name, setName] = useState('');
  const [draft, setDraft] = useState('');
  const [cart, setCart] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/public/carts/${encodeURIComponent(token)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'This cart link is not available.');
        setCart(null);
        return;
      }
      setError('');
      setCart(data);
    } catch {
      setError('Could not load this cart.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const saved = (localStorage.getItem(nameKey(token)) || '').trim();
    if (saved) setName(saved);
  }, [token]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [load]);

  const categories = useMemo(() => {
    const map = {};
    (cart?.categories || []).forEach((cat) => {
      if (cat?.id) map[cat.id] = cat;
    });
    return map;
  }, [cart]);

  const items = cart?.items || [];
  const open = items.filter((item) => !item.checked);
  const bought = items
    .filter((item) => item.checked)
    .sort((a, b) => String(b.checkedAt || '').localeCompare(String(a.checkedAt || '')));

  const saveName = (event) => {
    event.preventDefault();
    const next = draft.trim().split(/\s+/)[0] || '';
    if (!next) return;
    localStorage.setItem(nameKey(token), next);
    setName(next);
  };

  const toggle = async (item) => {
    if (!name || busyId) return;
    const buying = !item.checked;
    setBusyId(item.id);
    try {
      const res = await fetch(`${API_URL}/public/carts/${encodeURIComponent(token)}/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: item.id,
          firstName: name,
          bought: buying,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not update that item.');
      } else {
        setError('');
        setCart(data);
      }
    } catch {
      setError('Could not update that item.');
    } finally {
      setBusyId('');
    }
  };

  const renderItem = (item) => {
    const cat = categories[item.categoryId];
    const accent = colorFrom(cat?.colorValue);
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => toggle(item)}
        disabled={Boolean(busyId)}
        className="w-full text-left rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 flex gap-3 items-start hover:border-primary-400 transition-colors"
      >
        <span
          className={`mt-0.5 h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
            item.checked ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300'
          }`}
          style={!item.checked ? { borderColor: accent } : undefined}
        >
          {item.checked ? '✓' : ''}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span
              className={`font-semibold ${
                item.checked ? 'line-through text-gray-500' : 'text-gray-900 dark:text-white'
              }`}
            >
              {item.title}
            </span>
            {item.quantity ? (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">
                {item.quantity}
              </span>
            ) : null}
          </span>
          {item.checked && item.checkedByName ? (
            <span className="block text-xs font-semibold text-emerald-600 mt-1">
              Bought by {item.checkedByName}
            </span>
          ) : null}
          {item.note ? (
            <span className="block text-xs text-gray-500 mt-1">{item.note}</span>
          ) : null}
        </span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90 sticky top-0 z-10">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">Workloop cart</p>
            <h1 className="text-xl font-extrabold">{cart?.title || 'Cart'}</h1>
          </div>
          <Link to="/" className="text-sm font-semibold text-primary-600">
            WorkLoop
          </Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        {loading ? (
          <p className="text-sm text-gray-500">Loading cart…</p>
        ) : !cart ? (
          <div className="rounded-2xl bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700">
            <p className="font-semibold">This cart is not available.</p>
            <p className="text-sm text-gray-500 mt-2">{error || 'The link may have been turned off.'}</p>
          </div>
        ) : !name ? (
          <form onSubmit={saveName} className="rounded-2xl bg-white dark:bg-gray-800 p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-extrabold">What’s your first name?</h2>
            <p className="text-sm text-gray-500 mt-1">
              When you mark an item bought, everyone on this live cart will see it was bought by you.
            </p>
            <input
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="First name"
              className="input-field mt-4"
            />
            <button type="submit" className="btn-primary mt-4 w-full">
              Continue
            </button>
          </form>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                Shopping as <span className="font-bold text-gray-900 dark:text-white">{name}</span>
                {' · '}live
              </p>
              <button
                type="button"
                className="text-xs font-semibold text-primary-600"
                onClick={() => {
                  localStorage.removeItem(nameKey(token));
                  setName('');
                  setDraft('');
                }}
              >
                Change name
              </button>
            </div>
            {error ? <p className="text-sm text-rose-600 mb-3">{error}</p> : null}
            <div className="space-y-2">
              {open.map(renderItem)}
            </div>
            {bought.length > 0 ? (
              <>
                <h3 className="mt-8 mb-3 text-sm font-extrabold text-gray-500">Completed</h3>
                <div className="space-y-2">{bought.map(renderItem)}</div>
              </>
            ) : null}
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">No items on this cart yet.</p>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
